import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import api from '../utils/api';

const NAV_LINKS = [
  { path: '/roommates', label: 'Roommates' },
  { path: '/listings', label: 'Housing' },
  { path: '/subleases', label: 'Subleases' },
  { path: '/payments', label: 'Rent' },
  { path: '/messages', label: 'Messages' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { notifications } = useSocket();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [dbNotifs, setDbNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const profileRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => { fetchNotifications(); }, [notifications]);

  async function fetchNotifications() {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count'),
      ]);
      setDbNotifs(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch {}
  }

  useEffect(() => {
    function handleClick(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function markAllRead() {
    try {
      await api.put('/notifications/read-all');
      setUnreadCount(0);
      setDbNotifs(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  }

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-[#E8E8E8]" style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2.5 flex-shrink-0 group">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-9 h-9 flex-shrink-0">
              {/* Body */}
              <path d="M20 8 C14 8 10 13 10 18 C10 24 14 28 20 30 C26 28 30 24 30 18 C30 13 26 8 20 8Z" fill="#C8A800"/>
              {/* Wing sweep left */}
              <path d="M20 16 C16 14 10 15 6 12 C8 17 12 20 20 20Z" fill="#B89700"/>
              {/* Wing sweep right */}
              <path d="M20 16 C24 14 30 15 34 12 C32 17 28 20 20 20Z" fill="#B89700"/>
              {/* Head */}
              <ellipse cx="20" cy="10" rx="4.5" ry="4" fill="#C8A800"/>
              {/* Beak */}
              <path d="M22 10 L25.5 11.5 L22 12.5Z" fill="#1B1B1B"/>
              {/* Eye */}
              <circle cx="21" cy="9.5" r="1" fill="#1B1B1B"/>
              <circle cx="21.4" cy="9.2" r="0.35" fill="white"/>
              {/* Tail feathers */}
              <path d="M17 29 C15 32 13 34 11 35 C14 34 17 32 20 30 C23 32 26 34 29 35 C27 34 25 32 23 29Z" fill="#B89700"/>
              {/* Talons */}
              <path d="M17 30 L15 34 M17 30 L17 34.5 M17 30 L19 34" stroke="#1B1B1B" strokeWidth="0.8" strokeLinecap="round"/>
              <path d="M23 30 L21 34 M23 30 L23 34.5 M23 30 L25 34" stroke="#1B1B1B" strokeWidth="0.8" strokeLinecap="round"/>
            </svg>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-bold text-[#1B1B1B] tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>NestHawk</span>
              <span className="hidden sm:block text-[10px] text-[#AAAAAA] uppercase tracking-widest font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Iowa City</span>
            </div>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link, i) => (
              <React.Fragment key={link.path}>
                <Link
                  to={link.path}
                  className={`px-3 py-1.5 text-sm transition-colors nav-link ${
                    isActive(link.path)
                      ? 'text-[#C8A800] font-semibold'
                      : 'text-[#555555] font-medium hover:text-[#1B1B1B]'
                  }`}
                >
                  {link.label}
                </Link>
                {i < NAV_LINKS.length - 1 && (
                  <span className="text-[#E0E0E0] text-xs select-none">|</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">

            {/* Notifications bell */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) fetchNotifications(); }}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg text-[#777777] hover:text-[#1B1B1B] hover:bg-[#F4F4F4] transition-colors"
              >
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-3.5 h-3.5 bg-[#C8A800] text-white text-[9px] font-bold rounded-full flex items-center justify-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-[#E8E8E8] rounded-xl shadow-lg overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#F0F0F0]">
                    <span className="text-sm font-semibold text-[#1B1B1B]" style={{ fontFamily: 'Georgia, serif' }}>Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-xs text-[#C8A800] hover:text-[#B89700] nav-link">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {dbNotifs.length === 0 ? (
                      <p className="text-center text-[#AAAAAA] py-8 text-sm nav-link">No notifications</p>
                    ) : (
                      dbNotifs.map(n => {
                        const conversationId = n.data?.conversation_id;
                        const isMessage = n.type === 'new_message' && conversationId;

                        const handleNotifClick = async () => {
                          if (!n.read) {
                            try { await api.put(`/notifications/${n.id}/read`); } catch {}
                            setDbNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
                            setUnreadCount(prev => Math.max(0, prev - 1));
                          }
                          if (isMessage) {
                            setNotifOpen(false);
                            navigate(`/messages/${conversationId}`);
                          }
                        };

                        return (
                          <div
                            key={n.id}
                            onClick={isMessage ? handleNotifClick : undefined}
                            className={`px-5 py-3.5 border-b border-[#F5F5F5] last:border-0 transition-colors
                              ${!n.read ? 'bg-[#FDFAF0]' : ''}
                              ${isMessage ? 'cursor-pointer hover:bg-[#F5F0D8]' : ''}
                            `}
                          >
                            <div className="flex items-start gap-2.5">
                              {!n.read && <div className="w-1.5 h-1.5 bg-[#C8A800] rounded-full mt-1.5 flex-shrink-0" />}
                              <div className={!n.read ? 'flex-1' : 'pl-4 flex-1'}>
                                <p className="text-sm font-medium text-[#1B1B1B]">{n.title}</p>
                                <p className="text-xs text-[#777777] mt-0.5 leading-relaxed nav-link">{n.message}</p>
                                {isMessage && (
                                  <p className="text-xs text-[#C8A800] mt-1 font-medium nav-link">Tap to open conversation →</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-6 bg-[#E8E8E8]" />

            {/* Profile */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-[#F4F4F4] transition-colors"
              >
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt={user.name} className="w-7 h-7 rounded-full object-cover ring-1 ring-[#E8E8E8]" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[#C8A800] flex items-center justify-center text-white font-bold text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {user?.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <span className="hidden sm:block text-sm text-[#333333] font-medium nav-link">{user?.name?.split(' ')[0]}</span>
                <svg className="w-3.5 h-3.5 text-[#BBBBBB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white border border-[#E8E8E8] rounded-xl shadow-lg overflow-hidden">
                  <div className="px-4 py-3.5 border-b border-[#F0F0F0]">
                    <p className="font-semibold text-[#1B1B1B] text-sm">{user?.name}</p>
                    <p className="text-xs text-[#AAAAAA] truncate mt-0.5 nav-link">{user?.email}</p>
                  </div>
                  <Link to="/profile" onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#444444] hover:text-[#1B1B1B] hover:bg-[#F8F8F8] transition-colors nav-link">
                    <svg className="w-4 h-4 text-[#AAAAAA]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    My Profile
                  </Link>
                  {user?.user_type === 'landlord' && (
                    <Link to="/payments" onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#444444] hover:text-[#1B1B1B] hover:bg-[#F8F8F8] transition-colors nav-link">
                      <svg className="w-4 h-4 text-[#AAAAAA]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Stripe Connect
                    </Link>
                  )}
                  <div className="border-t border-[#F0F0F0]" />
                  <button onClick={() => { logout(); navigate('/'); setProfileOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:text-red-600 hover:bg-[#FFF8F8] transition-colors nav-link">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </nav>
  );
}
